import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavArrowLeft } from 'iconoir-react';
import ReactMarkdown from 'react-markdown';
import '../admin.css';

const UserDocumentation: React.FC = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>('introduction');
  const [content, setContent] = useState<string>('');

  useEffect(() => {
    // Charger le contenu du manuel utilisateur
    fetch('/MANUEL_UTILISATEUR.md')
      .then(response => response.text())
      .then(text => setContent(text))
      .catch(error => console.error('Erreur lors du chargement:', error));
  }, []);

  useEffect(() => {
    // Attendre que le contenu soit chargé avant d'initialiser l'observer
    if (!content) return;

    // Observer pour mettre à jour la section active lors du scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Ne changer la section active que si l'utilisateur a scrollé
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            if (scrollTop > 100) {
              setActiveSection(entry.target.id);
            }
          }
        });
      },
      { 
        threshold: 0.5,
        rootMargin: '-100px 0px -50% 0px' // Déclencher uniquement quand la section est bien visible
      }
    );

    // Attendre un peu que le DOM soit rendu
    setTimeout(() => {
      document.querySelectorAll('.doc-section').forEach((section) => {
        observer.observe(section);
      });
    }, 100);

    return () => observer.disconnect();
  }, [content]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sections = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'premiers-pas', label: 'Premiers pas' },
    { id: 'gestion-des-tableaux', label: 'Gestion des tableaux' },
    { id: 'gestion-des-colonnes', label: 'Gestion des colonnes' },
    { id: 'gestion-des-cartes', label: 'Gestion des cartes' },
    { id: 'étiquettes-et-filtres', label: 'Étiquettes et filtres' },
    { id: 'affectations-de-membres', label: 'Affectations de membres' },
    { id: 'commentaires-et-activités', label: 'Commentaires et activités' },
    { id: 'gestion-documentaire', label: 'Gestion documentaire' },
    { id: 'recherche-de-cartes', label: 'Recherche de cartes' },
    { id: 'gestion-des-membres', label: 'Gestion des membres' },
    { id: 'page-dadministration', label: "Page d'administration" },
    { id: 'rôles-et-permissions', label: 'Rôles et permissions' },
    { id: 'partage-de-cartes', label: 'Partage de cartes' },
    { id: 'cartes-archivées', label: 'Cartes archivées' },
    { id: 'profil-utilisateur', label: 'Profil utilisateur' },
    { id: 'conseils-et-bonnes-pratiques', label: 'Conseils et bonnes pratiques' },
    { id: 'raccourcis-clavier', label: 'Raccourcis clavier' },
    { id: 'dépannage', label: 'Dépannage' },
    { id: 'support', label: 'Support' },
    { id: 'notes-de-version', label: 'Notes de version' },
    { id: 'contact-et-support', label: 'Contact et support' },
  ];

  return (
    <div className="api-documentation">
      <div className="doc-header">
        <div>
          <h1>Documentation Utilisateur Ventilio</h1>
          <p>Guide complet pour utiliser toutes les fonctionnalités de Ventilio</p>
        </div>
        <button onClick={() => navigate('/')} className="btn-back">
          <NavArrowLeft width={20} height={20} />
          Retour
        </button>
      </div>

      <div className="doc-layout">
        {/* Sommaire */}
        <aside className="doc-sidebar">
          <nav className="doc-nav">
            <h3>Sommaire</h3>
            <ul>
              {sections.map((section) => (
                <li key={section.id} className={activeSection === section.id ? 'active' : ''}>
                  <a onClick={() => scrollToSection(section.id)}>{section.label}</a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <div className="doc-content user-doc-content">
          <ReactMarkdown
            components={{
              h2: ({ node, ...props }) => {
                const text = props.children?.toString() || '';
                const id = text
                  .toLowerCase()
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/(^-|-$)/g, '');
                return (
                  <section id={id} className="doc-section">
                    <h2 {...props} />
                  </section>
                );
              },
              h3: ({ node, ...props }) => <h3 className="doc-subsection" {...props} />,
              code: ({ node, ...props }) => {
                // Vérifier si c'est du code inline en regardant le parent
                const isInline = !props.className?.includes('language-');
                if (isInline) {
                  return <code {...props} />;
                }
                return (
                  <div className="code-block">
                    <pre>
                      <code {...props} />
                    </pre>
                  </div>
                );
              },
              ul: ({ node, ...props }) => <ul className="doc-list" {...props} />,
              ol: ({ node, ...props }) => <ol className="doc-list" {...props} />,
              img: ({ node, ...props }) => (
                <img {...props} style={{ maxWidth: '100%', borderRadius: '8px', margin: '20px 0' }} alt={props.alt || ''} />
              ),
              table: ({ node, ...props }) => (
                <div style={{ overflowX: 'auto', margin: '20px 0' }}>
                  <table className="error-codes-table" {...props} />
                </div>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default UserDocumentation;
